import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Star, 
  Heart, 
  MessageCircle, 
  Calendar,
  Search,
  Filter,
  ChevronDown,
  ThumbsUp,
  Edit,
  Trash2,
  AlertTriangle,
  Clock,
  X
} from 'lucide-react';

const Reviews = () => {
  const { reviews, bookings, addReview } = useData();
  const { user } = useAuth();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewData, setReviewData] = useState({
    score: 5,
    text: ''
  });
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  // Get completed bookings that can be reviewed
  const completedBookings = bookings.filter(booking => 
    booking.ownerId === user?.id && 
    booking.status === 'completed' &&
    !reviews.find(r => r.bookingId === booking.id)
  );

  // Get reviews written by the current user
  const userReviews = reviews.filter(review => review.byUserId === user?.id);

  // Calculate average rating
  const averageRating = userReviews.length > 0
    ? (userReviews.reduce((sum, review) => sum + review.score, 0) / userReviews.length).toFixed(1)
    : 0;

  // Rating distribution
  const ratingDistribution = Array.from({ length: 5 }, (_, i) => {
    const count = userReviews.filter(review => review.score === 5 - i).length;
    const percentage = userReviews.length > 0 ? (count / userReviews.length) * 100 : 0;
    return { stars: 5 - i, count, percentage };
  });

  const handleSubmitReview = (e) => {
    e.preventDefault();
    if (!selectedBooking || !reviewData.text.trim()) return;

    addReview({
      bookingId: selectedBooking.id,
      byUserId: user.id,
      toCaregiverId: selectedBooking.caregiverId,
      score: reviewData.score,
      text: reviewData.text.trim(),
      byUser: {
        name: user.name,
        avatar: user.avatar
      }
    });

    setReviewData({ score: 5, text: '' });
    setSelectedBooking(null);
    setShowReviewForm(false);
  };

  const renderStars = (rating, interactive = false, onRatingChange = null) => {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <button
            key={i}
            type={interactive ? 'button' : undefined}
            onClick={interactive && onRatingChange ? () => onRatingChange(i + 1) : undefined}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : ''} transition-transform`}
          >
            <Star
              className={`w-5 h-5 ${
                i < rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Reviews & Ratings
          </h1>
          <p className="text-gray-600">
            Share your experiences and read reviews from other pet parents
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Reviews Written</p>
                <p className="text-2xl font-bold text-gray-900">{userReviews.length}</p>
              </div>
              <div className="w-12 h-12 bg-[var(--pf-accent-soft)] rounded-xl flex items-center justify-center">
                <Edit className="w-6 h-6 text-[var(--pf-accent)]" />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Average Rating</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-gray-900">{averageRating}</p>
                  <Star className="w-6 h-6 text-yellow-400 fill-current" />
                </div>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending Reviews</p>
                <p className="text-2xl font-bold text-gray-900">{completedBookings.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Pending Reviews */}
            {completedBookings.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Pending Reviews</h2>
                  <span className="badge badge-warning">{completedBookings.length}</span>
                </div>
                <div className="space-y-4">
                  {completedBookings.map((booking) => (
                    <div key={booking.id} className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={booking.caregiver?.avatar}
                            alt={booking.caregiver?.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <h3 className="font-medium text-gray-900">{booking.caregiver?.name}</h3>
                            <p className="text-sm text-gray-600">{booking.pet?.name} • {formatDate(booking.end)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowReviewForm(true);
                          }}
                          className="btn-primary text-sm"
                        >
                          Write Review
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* My Reviews */}
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">My Reviews</h2>
                <div className="flex items-center gap-3">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="form-input py-1 pl-3 pr-8 text-sm"
                  >
                    <option value="recent">Most Recent</option>
                    <option value="rating-high">Highest Rated</option>
                    <option value="rating-low">Lowest Rated</option>
                  </select>
                </div>
              </div>

              {userReviews.length > 0 ? (
                <div className="space-y-6">
                  {userReviews.map((review) => (
                    <div key={review.id} className="p-6 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={review.byUser?.avatar}
                            alt={review.byUser?.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <h3 className="font-medium text-gray-900">{review.byUser?.name}</h3>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4" />
                              {formatDate(review.createdAt)}
                            </div>
                          </div>
                        </div>
                        {renderStars(review.score)}
                      </div>
                      <p className="text-gray-700 mb-4">{review.text}</p>
                      <div className="flex items-center justify-between">
                        <button className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1">
                          <ThumbsUp className="w-4 h-4" />
                          Helpful
                        </button>
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-gray-400 hover:text-[var(--pf-accent)] rounded-lg hover:bg-[var(--pf-accent-soft)]">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No reviews written yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Rating Distribution */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Rating Distribution</h3>
              <div className="space-y-3">
                {ratingDistribution.map(({ stars, count, percentage }) => (
                  <div key={stars} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 w-24">
                      {renderStars(stars)}
                    </div>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-16 text-sm text-gray-600 text-right">
                      {count} ({Math.round(percentage)}%)
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Review Guidelines */}
            <div className="pf-organic-card">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[var(--pf-accent)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Review Guidelines</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Be honest and constructive</li>
                    <li>• Focus on the care provided</li>
                    <li>• Mention specific experiences</li>
                    <li>• Avoid personal attacks</li>
                    <li>• Help others make informed decisions</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Review Form Modal */}
        {showReviewForm && selectedBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Write a Review</h3>
                <button
                  onClick={() => {
                    setShowReviewForm(false);
                    setSelectedBooking(null);
                    setReviewData({ score: 5, text: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={selectedBooking.caregiver?.avatar}
                    alt={selectedBooking.caregiver?.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{selectedBooking.caregiver?.name}</p>
                    <p className="text-sm text-gray-600">{selectedBooking.pet?.name}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmitReview} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rating
                  </label>
                  <div className="flex gap-1">
                    {renderStars(reviewData.score, true, (rating) => 
                      setReviewData({...reviewData, score: rating})
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review
                  </label>
                  <textarea
                    value={reviewData.text}
                    onChange={(e) => setReviewData({...reviewData, text: e.target.value})}
                    className="form-input h-32 resize-none"
                    placeholder="Share your experience with this caregiver..."
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowReviewForm(false);
                      setSelectedBooking(null);
                      setReviewData({ score: 5, text: '' });
                    }}
                    className="btn-outline flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                  >
                    Submit Review
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;